import {Injectable} from '@angular/core';
import {KeycloakEventType, KeycloakService} from 'keycloak-angular';
import {from, Observable, of, Subscription} from 'rxjs';
import {ConfigService} from '@shared/services';
import Keycloak, {KeycloakProfile} from 'keycloak-js';
import {map} from 'rxjs/operators';

const TOKEN_REFRESH_TIMEOUT_IN_MS = 2 * 60 * 1000;
const TOKEN_MIN_VALIDITY_IN_S = 30;

@Injectable({
	providedIn: 'root'
})
export class AuthenticationService {
	private tokenRefreshTimeout: any = null;
	private keycloakSub?: Subscription;
	private readonly keycloakEnabled: boolean;
	private readonly instance?: Keycloak;

	constructor(configService: ConfigService,
				private keycloakAngular: KeycloakService) {

		this.keycloakEnabled = configService.getConfig().keycloak.enabled;
		if (this.keycloakEnabled) {
			this.instance = keycloakAngular.getKeycloakInstance();
		}
	}

	init(): void {
		if (!this.keycloakEnabled) {
			return;
		}
		this.addExpiredListener();
		this.refreshToken();
	}

	unsubscribe(): void {
		this.keycloakSub?.unsubscribe();
	}

	logout(): void {
		if (!this.keycloakEnabled) {
			return;
		}
		this.instance?.logout();
	}

	private enableTokenRefresh(): void {
		if (this.tokenRefreshTimeout) {
			clearTimeout(this.tokenRefreshTimeout);
		}

		this.tokenRefreshTimeout = window.setTimeout(() => {
			this.tokenRefreshTimeout = null;
			this.refreshToken();
		}, TOKEN_REFRESH_TIMEOUT_IN_MS);
	}

	private refreshToken(): void {
		// If token is less than TOKEN_MIN_VALIDITY valid, it will be refreshed - automatically by the keycloak-library
		this.instance?.updateToken(TOKEN_MIN_VALIDITY_IN_S).then(() => {
			this.enableTokenRefresh();
		}).catch(() => {
			this.instance?.login();
		});
	}

	// Just as backup - should never trigger
	private addExpiredListener(): void {
		this.keycloakSub = this.keycloakAngular.keycloakEvents$.subscribe((event) => {
			if ((event as any).type === KeycloakEventType.TokenExpired) {
				this.refreshToken();
			}
		});
	}

	getUsername(): Observable<string | undefined> {
		if (!this.keycloakEnabled || !this.instance) {
			return of('');
		}
		return from(this.instance.loadUserProfile()).pipe(
			map((data: KeycloakProfile) => {
				return data.username;
			})
		);
	}

	getUserProfile(): Observable<KeycloakProfile | undefined> {
		if (!this.keycloakEnabled || !this.instance) {
			return of(undefined);
		}
		return from(this.instance.loadUserProfile());
	}

}
